import { useEffect, useMemo, useState } from "react";
import type { Team } from "@pagelink/shared";
import { api, ApiError, type Actor } from "./api.ts";
import { Avatar, Banner, Btn, Icon, Note } from "./ui.tsx";

type Person = { id: string; name: string; title: string; service: string };

/* ---------------------------------------------------------------------------
 * Teams: named groups (Code Blue, Trauma, ICU Rounding) that one page
 * reaches all at once. Every member gets their own copy of the page. Anyone
 * can make a team; its maker or an admin can change or remove it.
 * ------------------------------------------------------------------------ */
export function Teams({ me, onPage }: { me: Actor; onPage: (teamId: string) => void }) {
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [editing, setEditing] = useState<Team | "new" | null>(null);
  const [menu, setMenu] = useState<Team | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = () => api.teams().then((r) => setTeams(r.teams)).catch(() => setError("Could not load groups."));
  useEffect(() => { void load(); }, []);

  const run = async (fn: () => Promise<unknown>, said: string) => {
    setMenu(null);
    setError(null);
    try { await fn(); setDone(said); void load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "That did not work. Try again."); }
  };

  if (editing) {
    return <TeamEditor team={editing === "new" ? null : editing} onDone={() => { setEditing(null); void load(); }} />;
  }

  return (
    <>
      <h1 className="screen-title">Groups</h1>
      <p className="screen-sub">Page a whole group at once. Each member gets the page and answers it themselves.</p>
      <Btn full soft onClick={() => setEditing("new")} style={{ marginBottom: 12 }}><Icon name="plus" size={15} sw={2.4} />New group</Btn>
      {error && <Note warn>{error}</Note>}
      {done && !error && <Note>{done}</Note>}
      {teams && teams.length === 0 && <Note>No groups yet. Make one for people you page together, such as Code Blue or ICU Rounding.</Note>}
      {(teams ?? []).map((t) => {
        const others = t.members.filter((m) => m.id !== me.id).length;
        return (
          <div key={t.id} className="svcgroup" style={{ ["--div" as string]: "var(--accent-ink)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h3 style={{ flex: 1, margin: 0 }}>{t.name}</h3>
              <button className="btn soft sm" disabled={others === 0} onClick={() => onPage(t.id)}>
                <Icon name="pager" size={13} sw={2} />Page
              </button>
              <button className="icon-btn" aria-label={`More for ${t.name}`} onClick={() => { setDone(null); setMenu(t); }}>
                <Icon name="more" size={20} sw={2.6} />
              </button>
            </div>
            {t.description && <div className="tt" style={{ fontSize: 12.5, color: "var(--dim)", margin: "4px 0 2px" }}>{t.description}</div>}
            <div className="team-members">
              {t.members.map((m) => (
                <span key={m.id} className="team-chip"><Avatar id={m.id} name={m.name} size={18} fontSize={9} />{m.name}{m.id === me.id ? " (you)" : ""}</span>
              ))}
            </div>
          </div>
        );
      })}
      {menu && (
        <GroupMenu
          team={menu} me={me}
          onClose={() => setMenu(null)}
          onPage={() => { setMenu(null); onPage(menu.id); }}
          onEdit={() => { setMenu(null); setEditing(menu); }}
          onLeave={() => {
            if (window.confirm(`Leave ${menu.name}? You will stop getting its pages.`)) void run(() => api.leaveTeam(menu.id), `You left ${menu.name}.`);
          }}
          onDelete={() => {
            if (window.confirm(`Delete ${menu.name} for everyone? Pages already sent to it are kept.`)) void run(() => api.retireTeam(menu.id), `${menu.name} was deleted.`);
          }}
        />
      )}
    </>
  );
}

/** The "⋯" menu on a group: what this person may do with it. */
function GroupMenu({ team, me, onClose, onPage, onEdit, onLeave, onDelete }: {
  team: Team; me: Actor; onClose: () => void; onPage: () => void; onEdit: () => void; onLeave: () => void; onDelete: () => void;
}) {
  const canEdit = team.createdBy === me.id || me.isAdmin;
  const member = team.members.some((m) => m.id === me.id);
  const others = team.members.filter((m) => m.id !== me.id).length;
  const maker = team.members.find((m) => m.id === team.createdBy)?.name;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="scrim" onClick={onClose}>
      <div className="action-sheet" role="menu" aria-label={team.name} onClick={(e) => e.stopPropagation()}>
        <div className="as-head">{team.name}<span>{team.members.length} member{team.members.length === 1 ? "" : "s"}{member ? " · you are in it" : ""}</span></div>
        <button role="menuitem" disabled={others === 0} onClick={onPage}><Icon name="pager" size={17} sw={2} />Page this group</button>
        {canEdit
          ? <button role="menuitem" onClick={onEdit}><Icon name="edit" size={17} sw={2} />Edit name and members</button>
          : <div className="as-note">Only {maker ?? "its creator"} or an admin can edit this group.</div>}
        {member && (
          <button role="menuitem" disabled={team.members.length === 1} onClick={onLeave}>
            <Icon name="exit" size={17} sw={2} />Leave group
            {team.members.length === 1 && <small>You are the only member</small>}
          </button>
        )}
        {canEdit && <button role="menuitem" className="danger" onClick={onDelete}><Icon name="x" size={17} sw={2.2} />Delete group</button>}
        <button className="as-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function TeamEditor({ team, onDone }: { team: Team | null; onDone: () => void }) {
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [members, setMembers] = useState<Set<string>>(new Set(team?.members.map((m) => m.id) ?? []));
  const [people, setPeople] = useState<Person[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.clinicians().then((r) => setPeople(r.clinicians)).catch(() => {}); }, []);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return people.filter((p) => !t || `${p.name} ${p.title} ${p.service}`.toLowerCase().includes(t));
  }, [people, q]);

  const toggle = (id: string) => setMembers((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveTeam(team?.id ?? null, { name, description, memberIds: [...members] });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save the group.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!team || !window.confirm(`Delete ${team.name} for everyone? Pages already sent to it are kept.`)) return;
    setBusy(true);
    try { await api.retireTeam(team.id); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : "Could not remove the group."); setBusy(false); }
  };

  return (
    <>
      <button className="back" onClick={onDone}><Icon name="back" size={15} sw={2.2} />Groups</button>
      <h1 className="screen-title">{team ? `Edit ${team.name}` : "New group"}</h1>
      <div className="form" style={{ padding: 0 }}>
        {error && <Banner kind="err">{error}</Banner>}
        <div className="fsec">
          <label htmlFor="tm-name">Name</label>
          <input id="tm-name" type="text" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Code Blue" />
        </div>
        <div className="fsec">
          <label htmlFor="tm-desc">What it is for <span style={{ fontWeight: 400, color: "var(--faint)" }}>(optional)</span></label>
          <input id="tm-desc" type="text" value={description} maxLength={120} onChange={(e) => setDescription(e.target.value)} placeholder="Cardiac arrest response, 7th floor" />
        </div>
        <div className="fsec">
          <span className="flabel">Members · {members.size}</span>
          <div className="search">
            <span className="ic"><Icon name="search" size={16} /></span>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find people by name, role or service" aria-label="Find people" />
          </div>
          <div role="listbox" aria-multiselectable="true" aria-label="Group members">
            {shown.map((p) => (
              <button key={p.id} type="button" className="popt" role="option" aria-selected={members.has(p.id)} onClick={() => toggle(p.id)}>
                <Avatar id={p.id} name={p.name} size={30} fontSize={13} bg={members.has(p.id) ? "var(--accent-ink)" : undefined} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{p.name}</div>
                  <div className="tt"><span>{p.title} · {p.service}</span></div>
                </div>
                <span className={`check${members.has(p.id) ? " on" : ""}`} aria-hidden="true">{members.has(p.id) && <Icon name="check" size={13} sw={3} />}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="row">
          <Btn full soft tone="dim" disabled={busy} onClick={onDone}>Cancel</Btn>
          <Btn full disabled={busy || name.trim().length < 2 || members.size === 0} onClick={() => void save()}>{busy ? "Saving…" : "Save group"}</Btn>
        </div>
        {team && <div style={{ marginTop: 14 }}><Btn full sm soft tone="stat" disabled={busy} onClick={() => void remove()}><Icon name="x" size={14} sw={2.4} />Delete this group</Btn></div>}
      </div>
    </>
  );
}
